import { KoobMapMarker, KoobMapCard } from '@koob/maps';
import { KoobButton } from '@koob/ui';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '@koob/utils';
import { ProductTypeEnum } from '@koob/enums';
import { MUTED_PIN_COLOR, koobMarker } from '@koob/map-marker';
import { useSelectedType } from '@koob/trip-designer';
import { formatCurrency } from 'utils';
import { useExperienceSelection } from './ExperienceCard';

const typeIcon = {
  Activity: 'fa-person-hiking',
  AIR: 'fa-plane',
  LAND: 'fa-car',
  RAIL: 'fa-train',
  BOAT: 'fa-ship',
  Program: 'fa-route',
};

const ComposeAction = ({ experience, mode, onSelect, compatibility }) => {
  const { t } = useTranslation('experiences');
  const selectedType = useSelectedType();
  const { isSelectedExperienceInTrip, isBelowMinPax } = useExperienceSelection(
    experience,
    mode,
  );

  if (compatibility === 'incompatible') {
    return (
      <span className="text-xs font-medium text-red-600">
        {t('incompatible')}
      </span>
    );
  }

  return (
    <KoobButton
      size="sm"
      variant={compatibility === 'bestMatch' ? 'success' : 'primary'}
      onClick={() => onSelect(experience)}
      disabled={
        isSelectedExperienceInTrip ||
        isBelowMinPax ||
        selectedType !== ProductTypeEnum.EXPERIENCE
      }
    >
      {compatibility === 'bestMatch' && (
        <i className="fa-solid fa-shield-check" />
      )}
      {t(
        selectedType === ProductTypeEnum.EXPERIENCE
          ? 'misc:select'
          : 'tripDesigner:compose.selectDay',
      )}
    </KoobButton>
  );
};

const ExperiencePopupCard = ({ experience, mode, onSelect, compatibility }) => {
  const { t } = useTranslation('experiences');
  const type = experience?.transferType ?? experience?.experienceType;
  const { durationDays, durationHours, durationMinutes } = experience;
  const hasDuration =
    durationDays > 0 || durationHours > 0 || durationMinutes > 0;
  const hasPrice = experience?.price?.price !== undefined;

  let action = null;
  if (mode === 'compose') {
    action = (
      <ComposeAction
        experience={experience}
        mode={mode}
        onSelect={onSelect}
        compatibility={compatibility}
      />
    );
  } else if (!mode || mode === 'list') {
    action = (
      <KoobButton asChild size="sm" variant="primary">
        <Link to={`/experiences/edit/${experience?.id}`}>{t('misc:edit')}</Link>
      </KoobButton>
    );
  }

  return (
    <KoobMapCard
      image={getImageUrl(experience)}
      badge={
        <>
          <i className={`fa-solid ${typeIcon[type] ?? 'fa-route'}`} />
          {t('experienceTypology', {
            name:
              experience?.experienceType !== 'Activity'
                ? experience?.experienceType
                : experience?.isExtra === true
                  ? 'Extra'
                  : 'Activity',
          })}
        </>
      }
      title={experience?.name}
      subtitle={experience?.inCity?.title ?? experience?.startPosition?.name}
      facts={[
        hasDuration && (
          <>
            <i className="fa-light fa-clock" />
            {[
              durationDays > 0 &&
                t('durationUnitOptions.days', { count: durationDays }),
              durationHours > 0 &&
                t('durationUnitOptions.hours', { count: durationHours }),
              durationMinutes > 0 &&
                t('durationUnitOptions.minutes', { count: durationMinutes }),
            ]
              .filter(Boolean)
              .join(' ')}
          </>
        ),
      ]}
      warning={
        experience?.minPax > 1 &&
        t('minimumTravelers', { count: experience?.minPax })
      }
      price={
        hasPrice && (
          <span className="font-semibold text-orange-600">
            {formatCurrency({
              amount: experience?.price?.price,
              currency: experience?.price?.currency,
            })}
          </span>
        )
      }
      action={action}
    />
  );
};

const ExperienceMarker = ({
  experience,
  mode,
  onSelect,
  compatibility,
  lat,
  lng,
  typeOverride = null,
}) => {
  const latitude = parseFloat(
    '' + (lat ?? experience?.startPosition?.lat ?? experience?.inCity?.lat),
  );
  const longitude = parseFloat(
    '' + (lng ?? experience?.startPosition?.lon ?? experience?.inCity?.lon),
  );
  const type =
    typeOverride ?? experience.transferType ?? experience?.experienceType;

  const muted = compatibility === 'incompatible';
  const color = muted ? MUTED_PIN_COLOR : undefined;

  return (
    <KoobMapMarker
      lat={latitude}
      lng={longitude}
      // Pins already on the trip stay filled; incompatible ones are greyed.
      icon={koobMarker(type, { filled: mode === 'itinerary', color })}
      activeIcon={koobMarker(type, { filled: true, color })}
      muted={muted}
      popup={
        <ExperiencePopupCard
          experience={experience}
          mode={mode}
          onSelect={onSelect}
          compatibility={compatibility}
        />
      }
    />
  );
};

export default ExperienceMarker;
